import { shellGeometry } from "@papersflow/ui";
import { ProductBrand } from "./ProductBrand";

const desktopShellMinimum = shellGeometry.sidebarMin + shellGeometry.mainMin;

const loadingShellStyles = `
  .papersflow-loading-shell {
    grid-template-columns: minmax(0, 1fr);
  }

  .papersflow-loading-rail,
  .papersflow-loading-desktop-chrome {
    display: none;
  }

  @media (min-width: ${desktopShellMinimum}px) {
    .papersflow-loading-shell {
      grid-template-columns:
        clamp(
          ${shellGeometry.sidebarMin}px,
          calc(100vw - ${shellGeometry.mainMin}px),
          ${shellGeometry.sidebarDefault}px
        )
        minmax(${shellGeometry.mainMin}px, 1fr);
    }

    .papersflow-loading-rail {
      display: flex;
    }

    .papersflow-loading-mobile-brand {
      display: none;
    }

    .papersflow-loading-desktop-chrome {
      display: flex;
    }
  }

  @keyframes papersflow-workspace-opening {
    from { transform: translateX(-100%); }
    to { transform: translateX(350%); }
  }
`;

function StructuralLine({ width }: { readonly width: string }) {
  return (
    <span className="block h-1 rounded-sm bg-[var(--color-border-strong)]" style={{ width }} />
  );
}

/** Authenticated shell chrome shown while private workspace data remains gated. */
export function WorkspaceLoadingShell({ onReload }: { readonly onReload?: () => void }) {
  return (
    <main
      aria-busy="true"
      className="papersflow-loading-shell grid h-dvh min-h-dvh min-w-0 overflow-hidden bg-[var(--color-canvas)] font-shell text-[var(--color-foreground)]"
    >
      <style>{loadingShellStyles}</style>

      <aside className="papersflow-loading-rail min-w-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-sidebar)]">
        <header
          className="flex shrink-0 items-center border-b border-[var(--color-border)] px-3"
          style={{ height: shellGeometry.topbar }}
        >
          <ProductBrand compact />
        </header>

        <div aria-hidden="true" className="px-2 py-2">
          <div className="space-y-1">
            {["52%", "43%", "48%", "64%"].map((width) => (
              <div key={width} className="flex h-8 items-center px-2">
                <StructuralLine width={width} />
              </div>
            ))}
          </div>
          <div className="my-2 border-t border-[var(--color-border)]" />
          <div className="mb-1 flex h-7 items-center px-2">
            <StructuralLine width="26%" />
          </div>
          <div className="space-y-px">
            {["68%", "54%", "61%", "46%"].map((width) => (
              <div key={width} className="flex h-7 items-center pl-5 pr-2">
                <StructuralLine width={width} />
              </div>
            ))}
          </div>
        </div>
      </aside>

      <section className="flex min-w-0 flex-col bg-[var(--color-canvas)]">
        <header
          className="flex shrink-0 items-center border-b border-[var(--color-border)] px-3"
          style={{ height: shellGeometry.topbar }}
        >
          <span className="papersflow-loading-mobile-brand min-w-0">
            <ProductBrand compact />
          </span>
          <div
            aria-hidden="true"
            className="papersflow-loading-desktop-chrome min-w-0 flex-1 items-center justify-between"
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <StructuralLine width="72px" />
              <span className="text-[10px] text-[var(--color-border-strong)]">/</span>
              <StructuralLine width="104px" />
            </div>
            <div className="flex items-center gap-3">
              <StructuralLine width="42px" />
              <StructuralLine width="24px" />
            </div>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 place-items-center px-6 pb-[var(--workspace-topbar-height)]">
          <div className="w-full max-w-60">
            <div
              // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the loading contract requires one explicit status region for the visible announcement.
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <p className="text-xs font-semibold text-[var(--color-foreground)]">
                Opening your workspace
              </p>
              <p className="mt-1 text-[10px] text-[var(--color-foreground-muted)]">
                PapersFlow is preparing your private canvas.
              </p>
              {onReload ? (
                <p className="mt-3 text-[10px] leading-4 text-[var(--color-foreground-muted)]">
                  This is taking longer than expected.
                </p>
              ) : null}
            </div>
            <div aria-hidden="true" className="mt-3 h-px overflow-hidden bg-[var(--color-border)]">
              <span className="block h-px w-2/5 translate-x-1/2 bg-[var(--color-accent-bar)] motion-safe:animate-[papersflow-workspace-opening_1.1s_linear_infinite]" />
            </div>
            {onReload ? (
              <button
                type="button"
                className="mt-3 inline-flex h-8 items-center rounded-md border border-[var(--color-border)] px-3 text-[10px] font-semibold text-[var(--color-foreground)] hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]"
                onClick={onReload}
              >
                Reload PapersFlow
              </button>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}
